import type { UseRealtimeConnectionStatus } from 'inngest/react';
import { Message } from 'types/graphql';
import { v4 as uuidv4 } from 'uuid';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { VoiceTranscriptionTurn } from '../hooks/useAiVoiceTranscription';

type MessageState = Omit<Message, 'id'>;
type ConversationStatus = 'LOADING' | 'LOADED' | 'ERROR';

type ConversationNode = {
  id: number;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  metadata: { title?: string };
};

export type AttachedFile = {
  id: string;
  file?: File; // Optional for persisted files that are already uploaded
  uploading: boolean;
  url?: string;
  filename?: string;
  error?: string;
};

export type StatusEntryStatus = 'pending' | 'completed';
export type StatusEntryVariant = 'info' | 'success' | 'warning' | 'error';

export type StatusEntry = {
  status: StatusEntryStatus;
  message: string;
  label?: string; // Optional label like agent task name
  variant?: StatusEntryVariant; // Visual style variant (defaults to 'info')
  createdAt: number; // Timestamp for minimum display time
};

// Record<groupId, StatusEntry>
// e.g., { "agent-42": { status: "pending", message: "...", createdAt: 123 } }
export type StatusEntries = Record<string, StatusEntry>;

type AiChatState = {
  conversationId: number | null;
  conversationTitle: string | null;
  conversationCreatedAt: string | null;
  messages: MessageState[];
  streamingMessage: string | null;
  statusEntries: StatusEntries;
  realtimeState: UseRealtimeConnectionStatus;
  conversationStatus?: ConversationStatus;
  waitingForResponse?: boolean;
  messageEndCursor?: string | null;
  hasMoreMessages?: boolean;
  messageInput: string;
  messageParts: Record<number, VoiceTranscriptionTurn | string>;
  attachedFiles: AttachedFile[];
  historyExpanded?: boolean;
  conversationList: ConversationNode[];
  isVoiceRecording: boolean;
  debugModeEnabled: boolean;
  setWaitingForResponse: (waitingForResponse: boolean) => void;
  appendStreamingMessage: (delta: string) => void;
  clearStreamingMessage: () => void;
  finalizeStreamingMessage: (messageData: MessageState) => void;
  upsertStatus: (groupId: string, entry: Omit<StatusEntry, 'createdAt'>) => void;
  completeStatus: (groupId: string) => void;
  removeStatus: (groupId: string) => void;
  clearAllStatuses: () => void;
  getConversationId: () => number | null;
  addMessage: (message: MessageState) => void;
  setMessages: (messages: MessageState[]) => void;
  prependMessages: (messages: MessageState[]) => void;
  setConversationId: (conversationId: number, title?: string | null, createdAt?: string | null) => void;
  reset: () => void;
  setRealtimeState: (state: UseRealtimeConnectionStatus) => void;
  setConversationStatus: (status: ConversationStatus) => void;
  loadConversation: (
    id: number,
    messages: MessageState[],
    endCursor?: string | null,
    hasMore?: boolean,
    title?: string | null,
    createdAt?: string | null
  ) => Promise<void>;
  setMessagePagination: (endCursor: string | null, hasMore: boolean) => void;
  setMessageInput: (input: string) => void;
  setAttachedFiles: (files: AttachedFile[]) => void;
  addAttachedFiles: (files: File[]) => void;
  removeAttachedFile: (id: string) => void;
  updateAttachedFile: (id: string, updates: Partial<AttachedFile>) => void;
  toggleHistoryExpanded: () => void;
  setConversationList: (conversations: ConversationNode[]) => void;
  appendConversationList: (conversations: ConversationNode[]) => void;
  prependConversation: (conversation: ConversationNode) => void;
  getMessageParts: () => string;
  saveTranscriptionTurn: (turn: VoiceTranscriptionTurn) => void;
  endTranscription: () => void;
  setIsVoiceRecording: (isVoiceRecording: boolean) => void;
  getIsVoiceRecording: () => boolean;
  toggleDebugMode: () => void;
  updateConversationTitle: (title: string) => void;
};

const aiChatState = createStore(
  persist(
    immer<AiChatState>((set, get) => {
      return {
        conversationId: null,
        conversationTitle: null,
        conversationCreatedAt: null,
        messages: [],
        streamingMessage: null,
        statusEntries: {},
        realtimeState: 'closed',
        conversationStatus: undefined,
        messageInput: '',
        messageParts: {},
        isVoiceRecording: false,
        debugModeEnabled: false,
        appendStreamingMessage: (delta: string) => {
          set((state) => {
            if (state.streamingMessage === null) {
              state.streamingMessage = delta;
            } else {
              state.streamingMessage += delta;
            }
          });
        },
        clearStreamingMessage: () => {
          set((state) => {
            state.streamingMessage = null;
          });
        },
        finalizeStreamingMessage: (messageData: MessageState) => {
          set((state) => {
            // Check for duplicate by checksum
            if (messageData.checksum && state.messages.find((msg) => msg.checksum === messageData.checksum)) {
              state.streamingMessage = null;
              return;
            }
            // Add the finalized message and clear streaming state
            state.messages.push(messageData);
            state.streamingMessage = null;
          });
        },
        upsertStatus: (groupId: string, entry: Omit<StatusEntry, 'createdAt'>) => {
          set((state) => {
            // Preserve createdAt if entry exists, otherwise set new timestamp
            const existingCreatedAt = state.statusEntries[groupId]?.createdAt;
            state.statusEntries[groupId] = { ...entry, createdAt: existingCreatedAt ?? Date.now() };
          });
        },
        completeStatus: (groupId: string) => {
          set((state) => {
            if (state.statusEntries[groupId]) {
              state.statusEntries[groupId].status = 'completed';
            }
          });
        },
        removeStatus: (groupId: string) => {
          set((state) => {
            delete state.statusEntries[groupId];
          });
        },
        clearAllStatuses: () => {
          set((state) => {
            // Mark all as completed instead of removing - let auto-dismiss handle removal
            for (const key of Object.keys(state.statusEntries)) {
              state.statusEntries[key].status = 'completed';
            }
          });
        },
        getMessageParts: () => {
          const parts = get().messageParts;
          const existingInput = get().messageInput ?? '';
          const formattedParts = Object.values(parts)
            .map((part) => {
              return typeof part === 'string' ? part : part.words.map((word) => word.text).join(' ');
            })
            .join(' ');

          return existingInput + formattedParts;
        },
        saveTranscriptionTurn: (turn: VoiceTranscriptionTurn) => {
          set((state) => {
            if (turn.end_of_turn === true && turn.turn_is_formatted) {
              state.messageInput += turn.words.map((word) => word.text).join(' ') + ' ';
              delete state.messageParts[turn.turn_order];
              return;
            }

            state.messageParts[turn.turn_order] = turn;
          });
        },
        endTranscription: () => {
          set((state) => {
            for (const key in state.messageParts) {
              const part = state.messageParts[key];
              if (typeof part !== 'string') {
                state.messageInput += part.words.map((word) => word.text).join(' ') + ' ';
              } else {
                state.messageInput += part + ' ';
              }
            }

            state.messageParts = {};
          });
        },
        attachedFiles: [],
        conversationList: [],
        setConversationId: (conversationId: number, title?: string | null, createdAt?: string | null) => {
          set((state) => {
            state.conversationId = conversationId;
            state.conversationTitle = title ?? null;
            state.conversationCreatedAt = createdAt ?? new Date().toISOString();
          });
        },
        addMessage: (message: MessageState) => {
          set((state) => {
            if (message.checksum && state.messages.find((msg) => msg.checksum === message.checksum)) {
              return;
            }

            state.messages.push(message);
          });
        },
        setMessages: (messages: MessageState[]) => {
          set((state) => {
            state.messages = messages;
          });
        },
        prependMessages: (messages: MessageState[]) => {
          set((state) => {
            state.messages = [...messages, ...state.messages];
          });
        },
        setWaitingForResponse: (waitingForResponse: boolean) => {
          set((state) => {
            state.waitingForResponse = waitingForResponse;
          });
        },
        reset: () => {
          set((state) => {
            state.conversationId = null;
            state.conversationTitle = null;
            state.conversationCreatedAt = null;
            state.messages = [];
            state.streamingMessage = null;
            state.waitingForResponse = false;
            state.realtimeState = 'closed';
            state.messageEndCursor = null;
            state.hasMoreMessages = undefined;
            state.attachedFiles = [];
            state.statusEntries = {};
          });
        },
        getConversationId: () => {
          return get().conversationId;
        },
        setRealtimeState: (realtimeState: UseRealtimeConnectionStatus) => {
          set((state) => {
            state.realtimeState = realtimeState;
          });
        },
        setConversationStatus: (status: 'LOADING' | 'LOADED' | 'ERROR') => {
          set((state) => {
            state.conversationStatus = status;
          });
        },
        loadConversation: async (
          id: number,
          messages: MessageState[],
          endCursor?: string | null,
          hasMore?: boolean,
          title?: string | null,
          createdAt?: string | null
        ) => {
          set((state) => {
            state.conversationId = id;
            state.conversationTitle = title ?? null;
            state.conversationCreatedAt = createdAt ?? null;
            state.messages = messages;
            state.streamingMessage = null;
            state.statusEntries = {};
            state.conversationStatus = 'LOADED';
            state.messageEndCursor = endCursor ?? null;
            state.hasMoreMessages = hasMore ?? true;
          });
        },
        setMessagePagination: (endCursor: string | null, hasMore: boolean) => {
          set((state) => {
            state.messageEndCursor = endCursor;
            state.hasMoreMessages = hasMore;
          });
        },
        setMessageInput: (input: string) => {
          set((state) => {
            state.messageInput = input;
          });
        },
        setAttachedFiles: (files: AttachedFile[]) => {
          set((state) => {
            state.attachedFiles = files;
          });
        },
        addAttachedFiles: (files: File[]) => {
          set((state) => {
            // Create a key for each file using name + lastModified
            const getFileKey = (file: File) => `${file.name}-${file.lastModified}`;

            // Get existing file keys
            const existingKeys = new Set(state.attachedFiles.filter((f) => f.file).map((f) => getFileKey(f.file!)));

            // Filter out duplicates
            const uniqueFiles = files.filter((file) => !existingKeys.has(getFileKey(file)));

            const newFiles = uniqueFiles.map((file) => ({ id: uuidv4(), file, uploading: true }));

            state.attachedFiles = [...state.attachedFiles, ...newFiles];
          });
        },
        removeAttachedFile: (id: string) => {
          set((state) => {
            state.attachedFiles = state.attachedFiles.filter((f) => f.id !== id);
          });
        },
        updateAttachedFile: (id: string, updates: Partial<AttachedFile>) => {
          set((state) => {
            const file = state.attachedFiles.find((f) => f.id === id);
            if (file) {
              Object.assign(file, updates);
            }
          });
        },
        toggleHistoryExpanded: () => {
          set((state) => {
            state.historyExpanded = !state.historyExpanded;
          });
        },
        setConversationList: (conversations: ConversationNode[]) => {
          set((state) => {
            state.conversationList = conversations;
          });
        },
        appendConversationList: (conversations: ConversationNode[]) => {
          set((state) => {
            const existingIds = new Set(state.conversationList.map((c) => c.id));
            const newConversations = conversations.filter((c) => !existingIds.has(c.id));
            state.conversationList = [...state.conversationList, ...newConversations];
          });
        },
        prependConversation: (conversation: ConversationNode) => {
          set((state) => {
            // Don't add if already exists
            if (state.conversationList.some((c) => c.id === conversation.id)) {
              return;
            }
            state.conversationList = [conversation, ...state.conversationList];
          });
        },
        setIsVoiceRecording: (isVoiceRecording: boolean) => {
          set((state) => {
            state.isVoiceRecording = isVoiceRecording;
          });
        },
        getIsVoiceRecording: () => {
          return get().isVoiceRecording;
        },
        toggleDebugMode: () => {
          set((state) => {
            state.debugModeEnabled = !state.debugModeEnabled;
          });
        },
        updateConversationTitle: (title: string) => {
          set((state) => {
            // Update the current conversation title
            state.conversationTitle = title;

            // Also update in the conversation list
            const currentId = state.conversationId;
            if (currentId) {
              const conv = state.conversationList.find((c) => c.id === currentId);
              if (conv) {
                conv.title = title;
              }
            }
          });
        },
      };
    }),
    {
      name: 'ai-chat-session',
      version: 3,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        conversationId: state.conversationId,
        messages: state.messages,
        messageInput: state.messageInput,
        messageEndCursor: state.messageEndCursor,
        hasMoreMessages: state.hasMoreMessages,
        // Persist uploaded file URLs (but not the File objects themselves)
        attachedFiles: state.attachedFiles
          .filter((f) => f.url && !f.uploading && !f.error)
          .map((f) => ({ id: f.id, url: f.url, filename: f.filename, uploading: false })),
      }),
    }
  )
);

export function useAiChatStore<Selector extends (state: AiChatState) => unknown>(
  selector: Selector
): ReturnType<Selector> {
  return useStore(aiChatState, selector) as ReturnType<Selector>;
}
